package fyi.moko.hotpl.domain.model

/**
 * One axis of "how much of this place have I actually written down".
 *
 * Deliberately four coarse axes rather than a field count: the detail screen reacts to
 * *kinds* of detail (a memory, a face, a label, a story), not to how many characters
 * the user typed.
 */
enum class RichnessSignal {
    /** At least one recorded visit. */
    VISIT,

    /** A user-picked photo, not the derived category illustration. */
    PHOTO,

    /** At least one tag. */
    TAGS,

    /** A place note, or any visit that carries a note or an emotion. */
    STORY,
}

/**
 * How filled-in a place is, used by the detail screen to grow with the user's input.
 *
 * @property signals The axes that are currently filled.
 * @property isRegular True once the place has been visited often enough to read as a
 *   habit rather than a one-off (see `REGULAR_VISIT_THRESHOLD`).
 */
data class PlaceRichness(
    val signals: Set<RichnessSignal> = emptySet(),
    val isRegular: Boolean = false,
) {
    val filledCount: Int get() = signals.size

    /** 0f..1f, for the detail header's warmth — never read as a number by the user. */
    val fraction: Float get() = filledCount.toFloat() / RichnessSignal.entries.size

    operator fun contains(signal: RichnessSignal): Boolean = signal in signals

    /**
     * The same richness with [signal] unfilled. Lets a caller that knows better than the
     * calculator — e.g. a screen that tried to load the photo and found no file — correct
     * one axis without re-deriving the rest.
     */
    fun without(signal: RichnessSignal): PlaceRichness =
        if (signal in signals) copy(signals = signals - signal) else this

    companion object {
        val TOTAL_SIGNALS: Int = RichnessSignal.entries.size
    }
}
